import { defaultImageUrl } from '../config_and_helpers/config';
import { getShopProductApiId } from './shopCatalog';

/**
 * Normalized image list for product list rows (may only have `first_image`) and detail rows.
 *
 * @param {Object|null|undefined} product
 * @returns {Array<{ image_url: string }>}
 */
export function getProductImages(product) {
  if (!product) return [];
  if (Array.isArray(product.images) && product.images.length > 0) {
    return product.images;
  }
  if (product.first_image) {
    return [{ image_url: product.first_image }];
  }
  return [];
}

/**
 * Primary thumbnail URL for a product (list or detail).
 *
 * @param {Object|null|undefined} product
 * @param {string} [fallbackUrl=defaultImageUrl]
 * @returns {string}
 */
export function getProductImageUrl(product, fallbackUrl = defaultImageUrl) {
  const images = getProductImages(product);
  return images[0]?.image_url || fallbackUrl;
}

/**
 * Catalog number from API list or legacy camelCase field.
 *
 * @param {Object|null|undefined} product
 * @returns {string}
 */
export function getProductCatalogNumber(product) {
  if (!product) return '';
  return product.cat_num ?? product.catNum ?? '';
}

/**
 * Supplier id from list/detail product (numeric id field).
 *
 * @param {Object|null|undefined} product
 * @returns {number|string|null}
 */
export function getProductSupplierId(product) {
  if (!product) return null;
  if (product.supplier_id != null) {
    return product.supplier_id;
  }
  const s = product.supplier;
  if (s == null) return null;
  if (typeof s === 'object' && s.id != null) return s.id;
  return s;
}

/**
 * Integer product id for inventory APIs and cart (shop catalog uses primary_product_id).
 *
 * @param {Object|null|undefined} product
 * @returns {number|null}
 */
export function getProductInventoryId(product) {
  return getShopProductApiId(product);
}

/**
 * Aliquots location from API list/detail product shape.
 *
 * @param {Object|null|undefined} product
 * @returns {string}
 */
export function getProductAliquotsLocation(product) {
  if (!product) return '';
  const value = product.aliquots_location;
  if (value == null) return '';
  return String(value);
}

/**
 * Merge list `first_image` into detail `images` when detail omits images.
 *
 * @param {Object|null} current
 * @param {Object|null} fetched
 * @returns {Array<{ image_url: string }>}
 */
/**
 * Display unit quantity without trailing decimal zeros (e.g. 1 not 1.0000).
 *
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export function formatUnitQuantity(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (Number.isInteger(n)) return String(n);
  return String(parseFloat(n.toPrecision(12)));
}

export function mergeProductImages(current, fetched) {
  const fromFetched =
    Array.isArray(fetched?.images) && fetched.images.length > 0
      ? fetched.images
      : null;
  if (fromFetched) return fromFetched;

  const fromCurrent = getProductImages(current);
  if (fromCurrent.length > 0) return fromCurrent;

  return getProductImages(fetched);
}
